import { Link } from '@tanstack/react-router';

import { Badge } from '@/components/Badge/Badge';
import {
    Table,
    TableBody,
    TableCell,
    TableCellStack,
    TableHead,
    TableHeader,
    TableNum,
    TableRow,
} from '@/components/Table/Table';
import { type ClientsParams, clientsExportUrl, type ClientsReport } from '@/features/reports/api';
import { exportFileName, signalText } from '@/features/reports/clients/labels';
import { CLIENTS_COPY } from '@/features/reports/copy';
import { formatPercent, formatPounds, formatPoundsCompact } from '@/features/reports/format';
import { ReportCard, Tile } from '@/features/reports/parts';
import { CLIENT_SIGNAL } from '@/features/reports/tabs';
import { ExportButton } from '@/features/reports/transactions/ExportButton';
import { cn } from '@/util/utils';

const COPY = CLIENTS_COPY.spend;

/**
 * Clients › Spend, the prototype's Client 360 (ALS-87). The tiles are the prototype's four, over
 * the book as it is today; the table is the period's spend per client, the refunds settled to her
 * in it with VAT, what she actually kept, and her lifetime beside it (Franka, 22 Oct 2026). A row's
 * name opens her record.
 */
export function SpendView({ report, params }: { report: ClientsReport; params: ClientsParams }) {
    const { book, rows } = report.spend;
    const T = COPY.tiles;

    return (
        <>
            <div className="grid grid-cols-2 gap-3 xl:grid-cols-4">
                <Tile
                    label={T.active.label}
                    value={String(book.activeClients)}
                    note={T.active.note(book.activeClients, book.onTheBook)}
                    definition={T.active.definition}
                />
                <Tile
                    label={T.lifetime.label}
                    value={
                        book.averageLifetimeValue === null
                            ? '–'
                            : formatPoundsCompact(book.averageLifetimeValue)
                    }
                    note={T.lifetime.note}
                    definition={T.lifetime.definition}
                />
                <Tile
                    label={T.atRisk.label}
                    value={formatPoundsCompact(book.atRiskValue)}
                    note={T.atRisk.note(book.atRiskValue)}
                    definition={T.atRisk.definition(report.inactive.days)}
                />
                <Tile
                    label={T.repeat.label}
                    value={book.repeatRate === null ? '–' : formatPercent(book.repeatRate)}
                    note={T.repeat.note(book.repeatClients, book.orderedClients)}
                    definition={T.repeat.definition}
                />
            </div>

            <ReportCard
                title={COPY.card}
                subtitle={COPY.subtitle(report.current.label)}
                actions={
                    <ExportButton
                        url={clientsExportUrl('Spend', params)}
                        fileName={exportFileName(['spend', params.period])}
                        disabled={rows.length === 0}
                    />
                }
            >
                {rows.length === 0 ? (
                    <p className="text-muted-foreground py-4 text-sm">{COPY.empty}</p>
                ) : (
                    <Table stack region={COPY.card} className="table-stack--wide-labels">
                        <TableHeader>
                            <TableRow>
                                <TableHead>{COPY.columns.client}</TableHead>
                                <TableHead className="text-right">{COPY.columns.orders}</TableHead>
                                <TableHead className="text-right">{COPY.columns.total}</TableHead>
                                <TableHead className="text-right">{COPY.columns.refunds}</TableHead>
                                <TableHead className="text-right">{COPY.columns.actual}</TableHead>
                                <TableHead className="text-right">
                                    {COPY.columns.lifetime}
                                </TableHead>
                                <TableHead>{COPY.columns.signals}</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {rows.map((r) => (
                                <TableRow key={r.customerId}>
                                    <TableCell data-stack="title">
                                        <ClientName
                                            customerId={r.customerId}
                                            name={r.client}
                                            manager={r.relationshipManager}
                                            known={r.isKnown}
                                        />
                                    </TableCell>
                                    <TableNum data-stack="meta" data-label={COPY.columns.orders}>
                                        {r.orders}
                                    </TableNum>
                                    <TableNum data-stack="meta" data-label={COPY.columns.total}>
                                        {formatPounds(r.total)}
                                    </TableNum>
                                    <TableNum data-stack="meta" data-label={COPY.columns.refunds}>
                                        {r.refunds === 0 ? '–' : formatPounds(r.refunds)}
                                    </TableNum>
                                    <TableNum
                                        data-stack="status"
                                        className={cn(
                                            'font-medium',
                                            r.actualSpend < 0 && 'text-destructive',
                                        )}
                                    >
                                        {formatPounds(r.actualSpend)}
                                    </TableNum>
                                    <TableNum data-stack="meta" data-label={COPY.columns.lifetime}>
                                        {formatPounds(r.lifetime)}
                                    </TableNum>
                                    <TableCell data-stack="meta" data-label={COPY.columns.signals}>
                                        {r.signals.length === 0 ? (
                                            <span className="text-muted-foreground">–</span>
                                        ) : (
                                            <span className="flex flex-wrap gap-1">
                                                {r.signals.map((s) => (
                                                    <Badge
                                                        key={s.kind}
                                                        size="sm"
                                                        variant={
                                                            s.kind === CLIENT_SIGNAL.Inactive
                                                                ? 'warning'
                                                                : 'secondary'
                                                        }
                                                    >
                                                        {signalText(s)}
                                                    </Badge>
                                                ))}
                                            </span>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}
            </ReportCard>
        </>
    );
}

/**
 * The client's name opening her record, her relationship manager under it. A client the customer
 * table does not have is named without a link, since there is no record to open.
 */
export function ClientName({
    customerId,
    name,
    manager,
    known = true,
}: {
    customerId: number;
    name: string;
    manager: string | null;
    known?: boolean;
}) {
    if (!known) {
        return (
            <TableCellStack>
                <span className="text-muted-foreground font-medium">{name}</span>
                {manager ? <span className="text-xs">{manager}</span> : null}
            </TableCellStack>
        );
    }
    return (
        <TableCellStack>
            <Link
                to="/customers/$customerId"
                params={{ customerId: String(customerId) }}
                className="font-medium hover:underline pointer-coarse:inline-flex pointer-coarse:min-h-11 pointer-coarse:items-center"
            >
                {name}
            </Link>
            {manager ? <span className="text-xs">{manager}</span> : null}
        </TableCellStack>
    );
}
